import React from 'react';
import { Tabs, Tab, IconButton, Box, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { Close, HighlightOff } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiUtils } from '../utils/apiUtils';

function canonicalPath(pathname) {
  try {
    const u = new URL(pathname, 'http://local');
    const p = u.pathname.replace(/\/$/, '');
    const m = p.match(/^\/patient\/([^/]+)/);
    if (m) return `/patient/${m[1]}`; // collapse /patient/:id and /patient/:id/* to one tab
    return p || '/';
  } catch {
    const p = (pathname || '').replace(/\/$/, '');
    const m = p.match(/^\/patient\/([^/]+)/);
    if (m) return `/patient/${m[1]}`;
    return p || '/';
  }
}

function getPatientIdFromPath(pathname) {
  const p = canonicalPath(pathname);
  const parts = p.split('/').filter(Boolean);
  if (parts[0] === 'patient' && parts[1]) return decodeURIComponent(parts[1]);
  if ((parts[0] === 'preop-summary' || parts[0] === 'patient-summary') && parts[1]) {
    return decodeURIComponent(parts[1]);
  }
  if (parts[0] === 'patient' && parts[2] === 'summary' && parts[1]) {
    return decodeURIComponent(parts[1]);
  }
  return null;
}

function tabLabelFromPath(pathname) {
  const p = canonicalPath(pathname);
  if (!pathname || pathname === '/') return 'Home';
  const parts = p.split('/').filter(Boolean);
  if (
    (parts[0] === 'patient' && parts[1]) ||
    ((parts[0] === 'preop-summary' || parts[0] === 'patient-summary') && parts[1]) ||
    (parts[0] === 'patient' && parts[2] === 'summary' && parts[1])
  ) {
    return 'Loading...';
  }
  return parts.map(s => s.replace(/-/g, ' ')).join(' / ');
}

const patientNameCache = new Map();

async function fetchPatientName(patientId) {
  if (patientNameCache.has(patientId)) {
    return patientNameCache.get(patientId);
  }
  try {
    const response = await apiUtils.get(`/api/patients/${encodeURIComponent(patientId)}`);
    const patient = response.data || response;
    const name = patient?.firstName && patient?.lastName 
      ? `${patient.firstName} ${patient.lastName}`
      : patient?.name || `Patient ${patientId.slice(0, 8)}...`;
    patientNameCache.set(patientId, { name, mrn: patient?.mrn });
    return { name, mrn: patient?.mrn };
  } catch (err) {
    const fallback = { name: `Patient ${patientId.slice(0, 8)}...`, mrn: null };
    patientNameCache.set(patientId, fallback);
    return fallback;
  }
}

export default function EditorTabs() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [tabs, setTabs] = React.useState(() => {
    const startPath = canonicalPath(pathname || '/dashboard');
    return [{ path: startPath, label: tabLabelFromPath(startPath), patientId: getPatientIdFromPath(startPath) }];
  });
  const [patientNames, setPatientNames] = React.useState({});

  React.useEffect(() => {
    setTabs(prev => {
      const canon = canonicalPath(pathname);
      const patientId = getPatientIdFromPath(pathname);
      const seen = new Set();
      const deduped = [];
      for (const t of prev) {
        const c = canonicalPath(t.path);
        if (seen.has(c)) continue;
        seen.add(c);
        deduped.push({ ...t, path: c });
      }
      if (deduped.some(t => canonicalPath(t.path) === canon)) {
        return deduped.map(t => (canonicalPath(t.path) === canon ? { ...t, label: tabLabelFromPath(canon), patientId } : t));
      }
      return [...deduped, { path: canon, label: tabLabelFromPath(canon), patientId }];
    });
  }, [pathname]);

  React.useEffect(() => {
    const patientIds = tabs.map(t => t.patientId).filter(Boolean);
    if (patientIds.length === 0) return;
    
    const fetchNames = async () => {
      const results = {};
      const allNames = [];
      for (const id of patientIds) {
        const data = await fetchPatientName(id);
        results[id] = data;
        allNames.push({ ...data, id });
      }
      
      const processed = {};
      for (const id of patientIds) {
        const data = results[id];
        const sameNames = allNames.filter(n => n.name === data.name);
        if (sameNames.length > 1 && data.mrn) {
          processed[id] = `${data.name} (${data.mrn})`;
        } else {
          processed[id] = data.name;
        }
      }
      setPatientNames(processed);
    };
    fetchNames();
  }, [tabs]);

  const currentIndex = Math.max(0, tabs.findIndex(t => canonicalPath(t.path) === canonicalPath(pathname)));
  const hasMultipleTabs = tabs.length > 1;
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [menuTab, setMenuTab] = React.useState(null);

  const openContextMenu = (event, tab) => {
    event.preventDefault();
    setMenuAnchor(event.currentTarget);
    setMenuTab(tab);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuTab(null);
  };
  const openInSplit = orientation => {
    try {
      localStorage.setItem('wb_split', orientation);
      globalThis.dispatchEvent(
        new CustomEvent('wb:open-in-pane', {
          detail: { pane: orientation === 'vertical' ? 'right' : 'bottom', path: menuTab?.path },
        })
      );
    } catch (e) {
      /* noop */
    }
    closeMenu();
  };
  const moveToOtherPane = () => {
    try {
      globalThis.dispatchEvent(
        new CustomEvent('wb:move-current-to-secondary', { detail: { path: menuTab?.path } })
      );
    } catch (e) {
      /* noop */
    }
    closeMenu();
  };
  const swapPanes = () => {
    try {
      globalThis.dispatchEvent(new CustomEvent('wb:swap-panes'));
    } catch (e) {
      /* noop */
    }
    closeMenu();
  };

  const closeTab = idx => {
    setTabs(prev => {
      const next = prev.slice();
      const removed = next.splice(idx, 1)[0];
      if (!next.length) {
        const fallback = canonicalPath(pathname) || '/dashboard';
        const fallbackTab = { path: fallback, label: tabLabelFromPath(fallback) };
        if (removed?.path !== fallback) navigate(fallback);
        return [fallbackTab];
      }
      if (canonicalPath(removed?.path) === canonicalPath(pathname)) {
        const target = next[idx - 1] || next[idx] || next[next.length - 1];
        navigate(target.path);
      }
      return next;
    });
  };

  const closeAllTabs = React.useCallback(() => {
    const fallback = canonicalPath(pathname) || '/dashboard';
    setTabs([{ path: fallback, label: tabLabelFromPath(fallback) }]);
    closeMenu();
    if (canonicalPath(pathname) !== fallback) {
      navigate(fallback);
    }
  }, [navigate, pathname]);

  return (
    <Box
      sx={{
        borderBottom: theme => `1px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Tabs value={currentIndex} variant="scrollable" scrollButtons allowScrollButtonsMobile sx={{ flex: 1 }}>
        {tabs.map((t, i) => (
          <Tab
            key={t.path}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>{t.patientId && patientNames[t.patientId] ? patientNames[t.patientId] : t.label}</span>
                {tabs.length > 1 && (
                  <IconButton
                    component="span"
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      closeTab(i);
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label="Close tab"
                  >
                    <Close fontSize="inherit" />
                  </IconButton>
                )}
              </Box>
            }
            onClick={() => navigate(t.path)}
            onContextMenu={e => openContextMenu(e, t)}
            sx={{ textTransform: 'none', minHeight: 36, height: 36 }}
          />
        ))}
      </Tabs>
      <Tooltip title="Close all tabs">
        <span>
          <IconButton
            size="small"
            onClick={closeAllTabs}
            sx={{ mx: 1 }}
            disabled={!hasMultipleTabs}
          >
            <HighlightOff fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={() => openInSplit('vertical')}>Open in Split Right</MenuItem>
        <MenuItem onClick={() => openInSplit('horizontal')}>Open in Split Below</MenuItem>
        <MenuItem onClick={moveToOtherPane}>Move to Other Pane</MenuItem>
        <MenuItem onClick={swapPanes}>Swap Panes</MenuItem>
        <MenuItem onClick={closeAllTabs} disabled={!hasMultipleTabs}>
          Close All Tabs
        </MenuItem>
      </Menu>
    </Box>
  );
}
